/**
 * Central numerical tolerances for fold.app.
 *
 * Every epsilon in the codebase should come from here. If you find yourself
 * writing `if (d < 1e-9)` inline, add a named constant instead.
 *
 * The unit of length in the crease-pattern layer is "paper units". The default
 * sheet is 1 x 1, so a tolerance of 1e-9 is roughly a nanometre on an A4 sheet.
 */

/** Points closer than this (in paper units) are geometrically the same point. */
export const GEOMETRY_EPSILON = 1e-9;

/** Vertices closer than this get merged when planarising the crease pattern. */
export const VERTEX_MERGE_EPSILON = 1e-7;

/** Angles (radians) closer than this are considered equal. */
export const ANGLE_EPSILON = 1e-9;

/** Tolerance used when reporting whether a theorem residual "passes". */
export const THEOREM_TOLERANCE = 1e-6;

/** Triangles with |signed area| below this are degenerate. */
export const AREA_EPSILON = 1e-12;

/** Segments whose direction cross-product magnitude is below this are parallel. */
export const PARALLEL_EPSILON = 1e-12;

/** Convergence tolerance of the rigid-origami Gauss-Newton / Levenberg solver. */
export const SOLVER_TOLERANCE = 1e-8;

/** Hard cap on solver iterations, per solve call. */
export const MAX_SOLVER_ITERATIONS = 64;

/** Singular values below max(sv) * this are treated as numerically zero. */
export const RANK_RELATIVE_TOLERANCE = 1e-7;

/**
 * Fold angles are clamped to this magnitude. A perfectly flat fold is +-pi,
 * but the dihedral angle is discontinuous there (atan2 wraps), so the solver
 * targets 179.4 degrees instead. Documented in docs/MATHEMATICS.md.
 */
export const MAX_FOLD_ANGLE = Math.PI * 0.9966; // ~179.4 degrees

/** Minimum triangle height used when normalising dihedral gradients. */
export const MIN_HINGE_HEIGHT = 1e-6;

/** Screen-space pixel radius used for hit-testing in the 2D editor. */
export const PICK_RADIUS_PX = 8;

/** Snap radius in screen pixels for the CAD snapping engine. */
export const SNAP_RADIUS_PX = 12;

export const DEG = 180 / Math.PI;
export const RAD = Math.PI / 180;

export const toDegrees = (radians: number): number => radians * DEG;
export const toRadians = (degrees: number): number => degrees * RAD;

/** True when |a - b| <= tol. */
export function approxEqual(a: number, b: number, tol = GEOMETRY_EPSILON): boolean {
  return Math.abs(a - b) <= tol;
}

/** Wrap an angle into (-pi, pi]. */
export function wrapAngle(a: number): number {
  let x = (a + Math.PI) % (2 * Math.PI);
  if (x <= 0) x += 2 * Math.PI;
  return x - Math.PI;
}

/** Wrap an angle into [0, 2pi). */
export function wrapAngle2Pi(a: number): number {
  const x = a % (2 * Math.PI);
  return x < 0 ? x + 2 * Math.PI : x;
}

export function clamp(x: number, lo: number, hi: number): number {
  return x < lo ? lo : x > hi ? hi : x;
}

/** Numerically safe acos. */
export function safeAcos(x: number): number {
  return Math.acos(clamp(x, -1, 1));
}

export function isFiniteNumber(x: number): boolean {
  return Number.isFinite(x);
}
